// src/pages/Dashboard.js
import React, { useEffect, useState, useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { localApplicationsApi as api } from "../utils/api";
import { auditStatus, formatDue } from "../utils/dates";
import { SystemImageDisplay } from "../components/SystemImage";

const STATUS_LABEL = { ok: "Up to date", "due-soon": "Due soon", overdue: "Overdue", unknown: "Not set" };
const STATUS_BADGE = { ok: "badge-ok", "due-soon": "badge-due", overdue: "badge-overdue", unknown: "badge-neutral" };

const SYSTEM_TYPE_OPTIONS = ["Server App", "Local App", "Other"];

const SELECT_STYLE = {
  fontSize: 13,
  padding: "8px 12px",
  borderRadius: "var(--radius)",
  border: "none",
  background: "var(--gray-100)",
  color: "var(--gray-700)",
  cursor: "pointer",
  height: 36,
};

export default function Dashboard() {
  const [systems,    setSystems]    = useState([]);
  const [loading,    setLoading]    = useState(true);
  const [view,       setView]       = useState(() => localStorage.getItem("localapps_dash_view") || "card");
  const navigate = useNavigate();

  // Filters
  const [filterCategory,   setFilterCategory]   = useState("");
  const [filterAuthType,   setFilterAuthType]    = useState("");
  const [filterSystemType, setFilterSystemType]  = useState("");
  const [filterStatus,     setFilterStatus]      = useState("");
  const [sortBy,           setSortBy]            = useState("name");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await api.list();
      setSystems(data || []);
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);


  const handleEdit = (e, sys) => {
    e.stopPropagation();
    navigate(`/local-applications/${sys.id}/edit`);
  };

  // Unique filter options derived from data
  const categories  = useMemo(() => [...new Set(systems.map(s => s.category).filter(Boolean))].sort(), [systems]);
  const authTypes   = useMemo(() => [...new Set(systems.map(s => s.authType).filter(Boolean))].sort(), [systems]);

  const ownerNames = (sys) => (sys.accessOwners || []).map(o => o.displayName).join(", ");

  const filtered = useMemo(() => {
    let list = [...systems];
    if (filterCategory)   list = list.filter(s => s.category  === filterCategory);
    if (filterAuthType)   list = list.filter(s => s.authType  === filterAuthType);
    if (filterSystemType) list = list.filter(s => s.systemType === filterSystemType);
    if (filterStatus)     list = list.filter(s => auditStatus(s.nextAuditDue) === filterStatus);

    list.sort((a, b) => {
      switch (sortBy) {
        case "name":     return a.name.localeCompare(b.name);
        case "due":      return new Date(a.nextAuditDue) - new Date(b.nextAuditDue);
        case "status":   return auditStatus(a.nextAuditDue).localeCompare(auditStatus(b.nextAuditDue));
        case "category": return (a.category || "").localeCompare(b.category || "");
        case "users":    return (b.userCount || 0) - (a.userCount || 0);
        default:         return 0;
      }
    });
    return list;
  }, [systems, filterCategory, filterAuthType, filterSystemType, filterStatus, sortBy]);

  const counts = {
    all:        systems.length,
    overdue:    systems.filter(s => auditStatus(s.nextAuditDue) === "overdue").length,
    "due-soon": systems.filter(s => auditStatus(s.nextAuditDue) === "due-soon").length,
    ok:         systems.filter(s => auditStatus(s.nextAuditDue) === "ok").length,
  };

  const hasFilters = filterCategory || filterAuthType || filterSystemType || filterStatus;

  return (
    <div>
      {/* Header */}
      <div className="page-header">
        <div>
          <div className="page-title">Local Applications</div>
          <div className="page-subtitle">{systems.length} local application{systems.length !== 1 ? "s" : ""} registered</div>
        </div>
        <div style={{ display:"flex", gap:8, alignItems:"center" }}>
          <div style={{ display:"flex", background:"var(--gray-100)", borderRadius:"var(--radius)", overflow:"hidden", padding:2 }}>
            <button onClick={() => { setView("list"); localStorage.setItem("localapps_dash_view","list"); }} style={{ padding:"6px 12px", border:"none", cursor:"pointer", background: view==="list"?"var(--purple)":"#fff", color: view==="list"?"#fff":"var(--gray-600)", fontSize:13 }} title="List view">☰</button>
            <button onClick={() => { setView("card"); localStorage.setItem("localapps_dash_view","card"); }} style={{ padding:"6px 12px", border:"none", cursor:"pointer", background: view==="card"?"var(--purple)":"#fff", color: view==="card"?"#fff":"var(--gray-600)", fontSize:13 }} title="Card view">⊞</button>
          </div>
          <button className="btn-primary" onClick={() => navigate("/local-applications/new")}>+ Add Local Application</button>
        </div>
      </div>

      {/* Summary stats */}
      {!loading && systems.length > 0 && (
        <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 12, marginBottom: 20 }}>
          {[
            { key: "all",       label: "Total SaaS Applications", color: "var(--purple)", bg: "var(--purple-light)" },
            { key: "overdue",   label: "Overdue",       color: "var(--red)",    bg: "var(--red-light)"    },
            { key: "due-soon",  label: "Due Soon",      color: "var(--orange)", bg: "var(--orange-light)" },
            { key: "ok",        label: "Up to Date",    color: "var(--green)",  bg: "var(--green-light)"  },
          ].map(({ key, label, color, bg }) => (
            <div key={key}
              style={{ cursor: "pointer", background: bg, borderRadius: "var(--radius-lg)", padding: "18px", textAlign: "center" }}
              onClick={() => setFilterStatus(filterStatus === key || key === "all" ? "" : key)}>
              <div style={{ fontSize: 28, fontWeight: 600, color }}>{counts[key]}</div>
              <div style={{ fontSize: 12, color, opacity: 0.8, marginTop: 2 }}>{label}</div>
            </div>
          ))}
        </div>
      )}

      {/* Filters + Sort bar — 5 equal columns, single line */}
      {!loading && systems.length > 0 && (
        <div style={{ display: "grid", gridTemplateColumns: "repeat(5, 1fr)", gap: 8, marginBottom: 14, alignItems: "center" }}>
          <select style={{ ...SELECT_STYLE, width: "100%" }} value={filterStatus} onChange={e => setFilterStatus(e.target.value)}>
            <option value="">All statuses</option>
            <option value="overdue">Overdue</option>
            <option value="due-soon">Due Soon</option>
            <option value="ok">Up to Date</option>
          </select>

          <select style={{ ...SELECT_STYLE, width: "100%" }} value={filterCategory} onChange={e => setFilterCategory(e.target.value)}>
            <option value="">All categories</option>
            {categories.map(c => <option key={c} value={c}>{c}</option>)}
          </select>

          <select style={{ ...SELECT_STYLE, width: "100%" }} value={filterAuthType} onChange={e => setFilterAuthType(e.target.value)}>
            <option value="">All auth types</option>
            {authTypes.map(t => <option key={t} value={t}>{t}</option>)}
          </select>

          <select style={{ ...SELECT_STYLE, width: "100%" }} value={filterSystemType} onChange={e => setFilterSystemType(e.target.value)}>
            <option value="">All types</option>
            {SYSTEM_TYPE_OPTIONS.map(t => <option key={t} value={t}>{t}</option>)}
          </select>

          <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
            <select style={{ ...SELECT_STYLE, flex: 1 }} value={sortBy} onChange={e => setSortBy(e.target.value)}>
              <option value="name">Sort: Name A–Z</option>
              <option value="due">Sort: Due soonest</option>
              <option value="status">Sort: Status</option>
              <option value="category">Sort: Category</option>
              <option value="users">Sort: Most users</option>
            </select>
            {hasFilters && (
              <button className="btn-ghost" style={{ fontSize: 12, color: "var(--purple)", padding: "4px 6px", flexShrink: 0, whiteSpace: "nowrap" }}
                onClick={() => { setFilterCategory(""); setFilterAuthType(""); setFilterSystemType(""); setFilterStatus(""); }}>
                ✕
              </button>
            )}
          </div>
        </div>
      )}

      {/* Results count when filtering */}
      {hasFilters && !loading && (
        <div style={{ fontSize: 13, color: "var(--gray-500)", marginBottom: 10 }}>
          Showing {filtered.length} of {systems.length} local applications
        </div>
      )}

      {/* Systems table */}
      {loading ? (
        <div className="card" style={{ textAlign: "center", padding: 48 }}><span className="spinner" /></div>
      ) : filtered.length === 0 ? (
        <div className="card empty-state">
          <h3>{systems.length === 0 ? "No local applications yet" : "No local applications match these filters"}</h3>
          <p style={{ marginTop: 6 }}>
            {systems.length === 0 ? "Add your first local application to start tracking access." : "Try adjusting or clearing the filters."}
          </p>
          {systems.length === 0 && (
            <button className="btn-primary" style={{ marginTop: 16 }} onClick={() => navigate("/local-applications/new")}>+ Add Local Application</button>
          )}
        </div>
      ) : view === "card" ? (
        <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fill, minmax(200px, 1fr))", gap:14 }}>
          {filtered.map(sys => {
            const status = auditStatus(sys.nextAuditDue);
            const statusColor = status==="ok"?"var(--green)":status==="due-soon"?"var(--orange)":"var(--red)";
            return (
              <div key={sys.id} className="card" style={{ padding:0, overflow:"hidden", cursor:"pointer", transition:"box-shadow .15s" }}
                onClick={() => navigate(`/local-applications/${sys.id}`)}
                onMouseEnter={e => e.currentTarget.style.boxShadow="0 4px 12px rgba(0,0,0,.12)"}
                onMouseLeave={e => e.currentTarget.style.boxShadow=""}>
                <div style={{ display:"flex", alignItems:"center", gap:14, padding:"14px 16px" }}>
                  <SystemImageDisplay resource="local-applications" systemId={sys.id} systemName={sys.name} size={52} radius={8} cacheKey={sys.imageVersion} />
                  <div style={{ flex:1, minWidth:0 }}>
                    <div style={{ fontWeight:700, fontSize:14, marginBottom:2, whiteSpace:"nowrap", overflow:"hidden", textOverflow:"ellipsis" }}>{sys.name}</div>
                    {sys.category && <span className="badge badge-info" style={{ fontSize:10 }}>{sys.category}</span>}
                  </div>
                </div>
                <div style={{ padding:"10px 16px", display:"flex", alignItems:"center", justifyContent:"space-between" }}>
                  <div>
                    <div style={{ fontSize:12, color:"var(--gray-500)", marginBottom:3 }}>{ownerNames(sys)||"No owner"}</div>
                    <span className={`badge ${STATUS_BADGE[status]}`} style={{ fontSize:10 }}>{STATUS_LABEL[status]}</span>
                  </div>
                  <div style={{ display:"flex", gap:2 }}>
                    <button className="btn-ghost" style={{ fontSize:13, padding:"4px 6px" }} onClick={e => handleEdit(e, sys)} title="Edit">✏️</button>
                    <button className="btn-ghost" style={{ fontSize:13, padding:"4px 6px" }} onClick={e => { e.stopPropagation(); navigate(`/local-applications/${sys.id}/review`); }} title="Review">🔍</button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      ) : (
        <div className="card" style={{ padding: 0, overflow: "hidden" }}>
          <table style={{ width: "100%", borderCollapse: "collapse" }}>
            <thead>
              <tr style={{ background: "var(--gray-50)" }}>
                {["", "Name", "Category", "Type", "Auth Type", "Owner(s)", "Users", "Audit Due", "Status", ""].map(h => (
                  <th key={h} style={{ padding: "10px 12px", textAlign: "left", fontSize: 12, fontWeight: 600, color: "var(--gray-500)", whiteSpace: "nowrap" }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {filtered.map(sys => {
                const status = auditStatus(sys.nextAuditDue);
                return (
                  <tr key={sys.id} onClick={() => navigate(`/local-applications/${sys.id}`)}
                    style={{ cursor: "pointer" }}
                    onMouseEnter={e => e.currentTarget.style.background = "var(--gray-50)"}
                    onMouseLeave={e => e.currentTarget.style.background = ""}>
                    <td style={{ padding:"8px 4px 8px 12px", width:40 }}>
                      <SystemImageDisplay resource="local-applications" systemId={sys.id} systemName={sys.name} size={32} radius={4} cacheKey={sys.imageVersion} />
                    </td>
                    <td style={{ padding: "11px 12px" }}>
                      <div style={{ fontWeight: 500, fontSize: 14 }}>{sys.name}</div>
                      {sys.server && <div style={{ fontSize: 11, color: "var(--purple)", marginTop: 1 }}>{sys.server}</div>}
                    </td>
                    <td style={{ padding: "11px 12px" }}>
                      {sys.category ? <span className="badge badge-info" style={{ fontSize: 11 }}>{sys.category}</span> : <span style={{ color: "var(--gray-300)", fontSize: 12 }}>—</span>}
                    </td>
                    <td style={{ padding: "11px 12px" }}>
                      {sys.systemType ? <span className="badge badge-neutral" style={{ fontSize: 11 }}>{sys.systemType}</span> : <span style={{ color: "var(--gray-300)", fontSize: 12 }}>—</span>}
                    </td>
                    <td style={{ padding: "11px 12px", fontSize: 12, color: "var(--gray-600)" }}>
                      {sys.authType || <span style={{ color: "var(--gray-300)" }}>—</span>}
                    </td>
                    <td style={{ padding: "11px 12px", fontSize: 13 }}>
                      {ownerNames(sys) || <span style={{ color: "var(--gray-400)", fontSize: 12 }}>Not set</span>}
                    </td>
                    <td style={{ padding: "11px 12px", fontSize: 13, textAlign: "center" }}>
                      <strong>{sys.userCount || 0}</strong>
                    </td>
                    <td style={{ padding: "11px 12px", fontSize: 12 }}>
                      <div>{formatDue(sys.nextAuditDue)}</div>
                      <div style={{ fontSize: 11, color: "var(--gray-400)" }}>Every {sys.auditIntervalMonths}mo</div>
                    </td>
                    <td style={{ padding: "11px 12px" }}>
                      <span className={`badge ${STATUS_BADGE[status]}`}>{STATUS_LABEL[status]}</span>
                    </td>
                    <td style={{ padding: "11px 8px", whiteSpace: "nowrap" }}>
                      <button className="btn-ghost" style={{ fontSize: 13, padding: "4px 6px" }} onClick={e => handleEdit(e, sys)} title="Edit">✏️</button>
                      <button className="btn-ghost" style={{ fontSize: 13, padding: "4px 6px" }} onClick={e => { e.stopPropagation(); navigate(`/local-applications/${sys.id}/review`); }} title="Review">🔍</button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
